import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { ChevronDown, ChevronUp } from "lucide-react";

type Props = {
  upVotes: number;
  downVotes: number;
  wasUpvoted?: boolean;
  wasDownvoted?: boolean;
  onUpvote: () => void;
  onDownvote: () => void;
};

/*
  The card is one big link, so a vote has to call off the navigation as well as
  the bubble — otherwise voting also opens the set.
*/
const stop = (handler: () => void) => (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
  handler();
};

/**
 * How the set was received, as one number: the share that called it worth
 * doing, green from half the vote up and red below. The arrows are how you
 * change it, and the one you cast lights up.
 */
export const VoteChip = ({
  upVotes,
  downVotes,
  wasUpvoted,
  wasDownvoted,
  onUpvote,
  onDownvote,
}: Props) => {
  const { t } = useTranslation("sbcs");
  const up = upVotes || 0;
  const down = downVotes || 0;
  const total = up + down;
  const upShare = total > 0 ? Math.round((up / total) * 100) : 0;

  /* Not IconButton: these are smaller than a 30px control, so the arrows do
     not crowd the share between them. The corner is the IconButton's, so they
     still belong to the kebab's family. */
  const arrow = "size-6 rounded-[10px] border border-edge-strong p-0.5";

  return (
    /*
      Fixed width so a column that resizes cannot drag the reward text beside
      it, and right-aligned within it so the edge nearest the card is the one
      that holds still when the digits change. Sized to the share ("100%" is
      43px), which is the line that matters; a vote count past 99 runs a few
      pixels wider and overhangs into the band's gutter rather than onto the
      description.
    */
    <div className="flex w-11 flex-col items-end gap-y-1">
      <button
        type="button"
        title={t("toggle.worthIt")}
        aria-label={t("toggle.worthIt")}
        onClick={stop(onUpvote)}
        className={cn(
          arrow,
          wasUpvoted
            ? "bg-upvote-background text-positive-foreground"
            : "text-foreground-muted hover:text-foreground",
        )}
      >
        <ChevronUp className="size-full" />
      </button>
      <span
        className={cn(
          "font-oswald text-base font-medium leading-none tabular-nums",
          total === 0
            ? "text-foreground-faint"
            : upShare >= 50
              ? "text-positive-foreground"
              : "text-negative-foreground",
        )}
      >
        {upShare}%
      </span>
      <span className="whitespace-nowrap text-[10px] text-foreground-faint">
        {t("set.votes", { count: total })}
      </span>
      <button
        type="button"
        title={t("toggle.notWorthIt")}
        aria-label={t("toggle.notWorthIt")}
        onClick={stop(onDownvote)}
        className={cn(
          arrow,
          wasDownvoted
            ? "bg-downvote-background text-negative-foreground"
            : "text-foreground-muted hover:text-foreground",
        )}
      >
        <ChevronDown className="size-full" />
      </button>
    </div>
  );
};
